import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

export const GENERICPAGE_LEGACY_FIELD_ORDER = [
  "kiosk_display",
  { type: 'separator' },
  "background_color",
  "background_image",
  "background_image_position",
  "background_image_size",
  "background_repeat",
  "text_color",
  { type: 'separator' },
  "meta_description"
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  genericPageDescriptor: _generatedGenericPageDescriptor,
  genericPageBlocDefinition,
  genericPageDashboardLayout,
  genericPageConfig,
  genericPageResolveConfig,
  genericPageProperties,
} = doodBlockConfig(
  {
    name: "Generic page",
    internal_name: "Generic page",
    defaultParent: [/^Main Layout$/i],
    componentType: 'GenericPageComponent',
    category: 'page',
    description: "Generic page.",
    keywords: [],
    isRootBlock: true,
    sharedFields: {}
,
    slots: [
      {
        key: 'body',
        name: "pages.blocks.generic-page.body.label",
        accept: 'explicitOnly',
        allowedTypes: ["KioskHeaderBlock", ".*(?<!Page)"],
        slotItemFormat: 'ids',
      },
      {
        key: 'footer',
        name: "pages.blocks.generic-page.footer.label",
        accept: 'explicitOnly',
        allowedTypes: ["BigFooterBlock", "LegalsFooterBlock", "HtmlBlock"],
        slotItemFormat: 'ids',
      }
    ]
  },

  () => ({
    kiosk_display: propertyValue({
      type: 'checkbox',
      value: false,
      options: {
        label: "pages.blocks.generic-page.kiosk-display.label",
      }
    }),
    background_color: propertyValue({
      type: 'color',
      value: {"type":"hex","value":"#FFFFFF"},
      options: {
        label: "pages.blocks.generic-page.background-color.label",
      }
    }),
    background_image: propertyValue({
      type: 'image',
      value: '',
      options: {
        label: "pages.blocks.generic-page.background-image.label",
        help: "pages.blocks.generic-page.background-image.help",
      }
    }),
    background_image_position: propertyValue({
      type: 'select',
      value: 'center',
      options: {
        label: "pages.blocks.generic-page.background-image-position.label",
        help: "pages.blocks.generic-page.background-image-position.help",
        multiple: false,
        choices: [
          {
            value: 'center',
            label: "pages.blocks.generic-page.background-image-position.options.center",
          },
          {
            value: 'top',
            label: "pages.blocks.generic-page.background-image-position.options.top",
          },
          {
            value: 'bottom',
            label: "pages.blocks.generic-page.background-image-position.options.bottom",
          },
        ],
      }
    }),
    background_image_size: propertyValue({
      type: 'select',
      value: 'cover',
      options: {
        label: "pages.blocks.generic-page.background-image-size.label",
        help: "pages.blocks.generic-page.background-image-size.help",
        multiple: false,
        choices: [
          {
            value: 'cover',
            label: "pages.blocks.generic-page.background-image-size.options.cover",
          },
          {
            value: 'full-width',
            label: "pages.blocks.generic-page.background-image-size.options.full-width",
          },
        ],
      }
    }),
    background_repeat: propertyValue({
      type: 'select',
      value: 'no-repeat',
      options: {
        label: "pages.blocks.generic-page.background-repeat.label",
        help: "pages.blocks.generic-page.background-repeat.help",
        multiple: false,
        choices: [
          {
            value: 'no-repeat',
            label: "pages.blocks.generic-page.background-repeat.options.no-repeat",
          },
          {
            value: 'repeat',
            label: "pages.blocks.generic-page.background-repeat.options.repeat",
          },
          {
            value: 'repeat-x',
            label: "pages.blocks.generic-page.background-repeat.options.repeat-x",
          },
          {
            value: 'repeat-y',
            label: "pages.blocks.generic-page.background-repeat.options.repeat-y",
          },
          {
            value: 'space',
            label: "pages.blocks.generic-page.background-repeat.options.space",
          },
          {
            value: 'round',
            label: "pages.blocks.generic-page.background-repeat.options.round",
          },
        ],
      }
    }),
    text_color: propertyValue({
      type: 'color',
      value: {"type":"hex","value":"#00000"},
      options: {
        label: "pages.blocks.generic-page.text-color.label",
      }
    }),
    meta_description: propertyValue({
      type: 'textarea',
      value: '',
      options: {
        label: "pages.blocks.generic-page.meta-description.label",
        help: "pages.blocks.generic-page.meta-description.help",
        rows: 3,
      }
    })
  }),

  () => ({
    omitFields: ["meta_description"] as const,
    tabs: [
      {
        label: "pages.blocks.generic-page.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.generic-page.fieldset.appearance.label",
            fields: [
              "background_color",
              "background_image",
              {
                prop: "background_image_position" as const,
                visibleWhen: {
                  prop: "background_image" as const,
                  nonEmpty: true,
                },
              },
              {
                prop: "background_image_size" as const,
                visibleWhen: {
                  prop: "background_image" as const,
                  nonEmpty: true,
                },
              },
              {
                prop: "background_repeat" as const,
                visibleWhen: {
                  prop: "background_image" as const,
                  nonEmpty: true,
                },
              },
              "text_color",
            ],
          },
          {
            legend: "pages.blocks.generic-page.fieldset.blocks.label",
            fields: ["body", "footer"],
          },
          {
            legend: "pages.blocks.generic-page.fieldset.kiosk.label",
            fields: ["kiosk_display"],
          },
        ],
      },
    ],
  })
);

export function genericPageDescriptor(): IBlockDescriptor;
export function genericPageDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function genericPageDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedGenericPageDescriptor;
  }
  return buildOrderedBlockDescriptor(_generatedGenericPageDescriptor, genericPageProperties, fieldOrder);
}

export {
  genericPageBlocDefinition,
  genericPageDashboardLayout,
  genericPageProperties,
  genericPageConfig,
  genericPageResolveConfig,
};

export type GenericPageConfig = typeof genericPageConfig;
